import { useEffect } from 'react';
import { captureException } from '@sentry/nextjs';
import { useDispatch, useSelector } from 'src/store/store';
import { fetchCurrentCommunity } from 'src/modules/shared/context/actions';
import { setAlert } from 'src/components/alert/actions';
import { fetchMembers } from 'src/modules/community/communityMembers/slice';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';
import { getCommunityMembersSeo } from 'src/utils/seo';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Element, Section } from 'src/constants/footfall';
import { CommunityLayout } from 'src/components/layouts';
import CommunityMembersContent from 'src/modules/community/communityMembers/communityMembersContent';
import { PageContext } from 'src/constants/types';
type Props = {
  filter?: string;
  page?: number;
  utmSource?: string | null;
};
function CommunityMembersPage({
  filter = 'similar-to-me',
  page = 1,
  utmSource = null
}: Props) {
  const dispatch = useDispatch();
  const {
    currentCommunity,
    currentUser
  } = useSelector(({
    context
  }) => context);
  const isCurrentUserProfileLoading = useSelector(({
    currentUserProfile
  }) => currentUserProfile.isLoading);
  const isLoggedIn = !!currentUser;
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchMembers({
          filter,
          page
        })).unwrap();
      } catch (err) {
        dispatch(setAlert());
        captureException(err);
      }
    };
    if (isLoggedIn) {
      fetchData();
    }
  }, [dispatch, filter, isCurrentUserProfileLoading, isLoggedIn, page]);
  useEffect(() => {
    // For visitor, show the sign up modal or the login modal (see handleQueryParams)
    if (!isLoggedIn && utmSource !== 'notification') {
      // event when sign up modal is displayed
      trackFootfallEvent(Event.Clicked, {
        clickType: 'sign-up',
        clickMetadata: {
          section: Section.CommunityMembers,
          element: Element.MemberTab
        }
      });
      dispatch(showSignupModal({
        community: currentCommunity
      }));
    }
  }, [isLoggedIn, utmSource, currentCommunity, dispatch]);
  return <CommunityLayout currentPage="members" fullWidth={true} seo={getCommunityMembersSeo(currentCommunity)} data-sentry-element="CommunityLayout" data-sentry-component="CommunityMembersPage" data-sentry-source-file="members.tsx">
      <CommunityMembersContent data-sentry-element="CommunityMembersContent" data-sentry-source-file="members.tsx" />
    </CommunityLayout>;
}
CommunityMembersPage.getInitialProps = async ({
  store: {
    dispatch,
    getState
  },
  query: {
    filter,
    page,
    communitySlug,
    utm_source
  }
}: PageContext) => {
  await dispatch(fetchCurrentCommunity(communitySlug));
  const {
    currentCommunity,
    communityErrorStatusCode,
    currentUser
  } = getState().context;
  if (!currentCommunity) {
    const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
    if (isHiddenPrivateCommunity) {
      if (!currentUser) throw new RequestError('This page is not available to visitors', 401);
      throw new RequestError('This is a private community', 404); // user is not a member
    }
    throw new RequestError('Error fetching community', communityErrorStatusCode);
  }
  return {
    filter,
    page: parseInt(page),
    utmSource: utm_source
  };
};
export default WithErrorHandling(CommunityMembersPage);